import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Chip } from '@mui/material';
import { Send, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const HEADING = '#fafafa';
const BODY = '#d4d4d8';
const SECONDARY = '#a1a1aa';
const GOLD = '#fbbf24';
const GREEN = '#4ade80';
const CARD_RAD = '14px';

const AssignmentSubmission = ({ courseId, sectionIndex, lessonIndex, onSubmitted }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

  const handleSubmit = async () => {
    if (!text.trim()) {
      toast.error('Please write your submission first');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/api/content/course/${courseId}/lesson/assignment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionIndex, lessonIndex, submission: text.trim() }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      toast.success('Assignment submitted!');
      onSubmitted?.();
    } catch {
      toast.error('Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 3, p: { xs: 2, md: 2.5 }, border: '1px solid rgba(251,191,36,0.15)', borderRadius: CARD_RAD, bgcolor: 'rgba(251,191,36,0.03)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 15 }}>Submit Your Work</Typography>
        {submitted && (
          <Chip icon={<CheckCircle size={12} />} label="Submitted" size="small"
            sx={{ bgcolor: 'rgba(74,222,128,0.1)', color: GREEN, '& .MuiChip-icon': { color: GREEN } }} />
        )}
      </Box>
      <Typography sx={{ color: SECONDARY, fontSize: 13, mb: 2, lineHeight: 1.6 }}>
        Share your solution, reflection, or project output below. Your instructor can review submissions.
      </Typography>
      <TextField
        multiline
        rows={5}
        fullWidth
        placeholder="Write your assignment response…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={submitted}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '10px', color: BODY,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(251,191,36,0.4)' },
          },
        }}
      />
      <Button
        startIcon={<Send size={16} />}
        onClick={handleSubmit}
        disabled={submitting || submitted}
        variant="contained"
        sx={{
          bgcolor: submitted ? 'rgba(74,222,128,0.15)' : 'rgba(251,191,36,0.2)',
          color: submitted ? GREEN : GOLD,
          border: submitted ? 'none' : `1px solid rgba(251,191,36,0.3)`,
          textTransform: 'none', borderRadius: '10px', fontWeight: 700,
          boxShadow: 'none', '&:hover': { bgcolor: 'rgba(251,191,36,0.3)' },
        }}
      >
        {submitted ? 'Submitted' : submitting ? 'Submitting…' : 'Submit Assignment'}
      </Button>
    </Box>
  );
};

export default AssignmentSubmission;